import { safeLocalStorage } from '@/lib/safeStorage';
import { getApiUrl } from './api/core';

/**
 * Social sign-in for the office app (RUT-4523): Google, and Apple (fast
 * signup). Another provider reuses every piece here and only adds a button.
 *
 * Apple may hand over a private relay address (`…@privaterelay.appleid.com`)
 * instead of the person's own. It is a real, verified inbox that forwards to
 * them, so it is the account's email like any other; Ruta's mail reaches it
 * once `getruta.com` is registered with Apple's Private Email Relay.
 *
 * Flow: the button sends the browser to Cognito's `/oauth2/authorize` with the
 * provider named (so Cognito's own page is skipped), the authorization-code
 * grant and PKCE. Cognito returns to `/auth/callback` with a code, which the
 * callback page trades for tokens. No client secret is involved (the webapp
 * client has none) and tokens never ride in a URL.
 *
 * Where to sign in comes from `GET /health` (`socialSignIn`), so a stack with
 * no provider configured shows no buttons and needs no build variable.
 */

/** Cognito's provider names, as `GET /health` lists them in `socialSignIn.providers`. */
export type SocialProvider = 'Google' | 'SignInWithApple';

/** What the person calls each provider. */
export const SOCIAL_PROVIDER_LABEL: Record<SocialProvider, string> = {
  Google: 'Google',
  SignInWithApple: 'Apple',
};

/** Where the person was headed and why, kept across the round trip to Google. */
export type SocialSignInIntent =
  | { kind: 'login' }
  | { kind: 'signup' }
  | { kind: 'invite'; inviteToken: string };

export interface SocialSignInConfig {
  host: string;
  clientId: string;
  providers: string[];
}

/** One sign-in in flight; sessionStorage so it dies with the tab. */
const PENDING_KEY = 'ruta_social_sign_in';

/** Values a page hands to /auth/callback across the round trip to Google. */
export const SOCIAL_SESSION_KEYS = {
  /** The phone typed on the invite page; the invite still needs it. */
  invitePhone: 'ruta_invite_phone',
  /** utm_* / gclid from the signup URL; Google returns without the query. */
  signupAttribution: 'ruta_signup_attribution',
} as const;
/** After a provider identity is linked to an existing account, retry once. */
const LINK_RETRY_KEY = 'ruta_social_link_retried';

interface PendingSignIn {
  state: string;
  verifier: string;
  provider: SocialProvider;
  intent: SocialSignInIntent;
  startedAt: number;
}

let configPromise: Promise<SocialSignInConfig | null> | null = null;

/** The stack's social sign-in settings, or null when none is configured. */
export function loadSocialSignInConfig(fetchImpl: typeof fetch = fetch): Promise<SocialSignInConfig | null> {
  configPromise ??= (async () => {
    try {
      const res = await fetchImpl(`${getApiUrl()}/health`);
      if (!res.ok) return null;
      const body = (await res.json()) as { socialSignIn?: SocialSignInConfig };
      const cfg = body.socialSignIn;
      if (!cfg?.host || !cfg.clientId || !Array.isArray(cfg.providers)) return null;
      return cfg;
    } catch {
      return null;
    }
  })();
  return configPromise;
}

/** Test seam. */
export function resetSocialSignInConfigCache() {
  configPromise = null;
}

export function callbackUrl(origin: string = window.location.origin): string {
  return `${origin}/auth/callback`;
}

function base64Url(bytes: Uint8Array): string {
  let s = '';
  bytes.forEach((b) => { s += String.fromCharCode(b); });
  return window.btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(byteLength = 32): string {
  const bytes = new Uint8Array(byteLength);
  window.crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

export async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await window.crypto.subtle.digest('SHA-256', new window.TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

export async function buildAuthorizeUrl(
  config: SocialSignInConfig,
  provider: SocialProvider,
  state: string,
  verifier: string,
  origin?: string,
): Promise<string> {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: callbackUrl(origin),
    identity_provider: provider,
    scope: 'openid email profile',
    state,
    code_challenge: await pkceChallenge(verifier),
    code_challenge_method: 'S256',
  });
  return `https://${config.host}/oauth2/authorize?${params.toString()}`;
}

/** Remember the sign-in and send the browser to the provider. */
export async function startSocialSignIn(
  config: SocialSignInConfig,
  provider: SocialProvider,
  intent: SocialSignInIntent,
  navigate: (url: string) => void = (url) => window.location.assign(url),
): Promise<void> {
  const pending: PendingSignIn = {
    state: randomString(),
    verifier: randomString(48),
    provider,
    intent,
    startedAt: Date.now(),
  };
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  navigate(await buildAuthorizeUrl(config, provider, pending.state, pending.verifier));
}

/**
 * The sign-in in flight, if `state` is the one this tab sent. Cleared only on
 * a match, so a crafted /auth/callback link cannot cancel a real sign-in.
 */
export function takePendingSignIn(state: string | null): PendingSignIn | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw || !state) return null;
    const pending = JSON.parse(raw) as PendingSignIn;
    if (pending.state !== state) return null;
    sessionStorage.removeItem(PENDING_KEY);
    // A sign-in left open for an hour is not this one.
    if (!pending.state || !pending.verifier || Date.now() - pending.startedAt > 60 * 60 * 1000) return null;
    return pending;
  } catch {
    return null;
  }
}

export interface SocialTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

/** Trade the authorization code for tokens (public client + PKCE). */
export async function exchangeCode(
  config: SocialSignInConfig,
  code: string,
  verifier: string,
  fetchImpl: typeof fetch = fetch,
  origin?: string,
): Promise<SocialTokens> {
  const res = await fetchImpl(`https://${config.host}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.clientId,
      code,
      redirect_uri: callbackUrl(origin),
      code_verifier: verifier,
    }).toString(),
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, string>;
  if (!res.ok || !body.id_token || !body.access_token || !body.refresh_token) {
    throw new Error(body.error_description || body.error || 'Token exchange failed');
  }
  return { idToken: body.id_token, accessToken: body.access_token, refreshToken: body.refresh_token };
}

/**
 * What Cognito's `error_description` means. The pre-sign-up trigger
 * (`apps/api/src/handlers/auth/preSignUp.ts`) throws these codes; Cognito
 * wraps them as "PreSignUp failed with error <CODE>."
 */
export type SocialSignInOutcome =
  | 'cancelled'
  | 'linked'
  | 'email_not_verified'
  | 'finish_email_signup'
  | 'prove_email'
  | 'account_disabled'
  | 'use_crew_app'
  | 'customer_email'
  | 'failed';

export function classifyCallbackError(error: string | null, description: string | null): SocialSignInOutcome {
  const d = description ?? '';
  if (d.includes('RUTA_ACCOUNT_LINKED')) return 'linked';
  if (d.includes('RUTA_EMAIL_NOT_VERIFIED')) return 'email_not_verified';
  if (d.includes('RUTA_FINISH_EMAIL_SIGNUP')) return 'finish_email_signup';
  if (d.includes('RUTA_PROVE_EMAIL')) return 'prove_email';
  if (d.includes('RUTA_ACCOUNT_DISABLED')) return 'account_disabled';
  if (d.includes('RUTA_USE_CREW_APP')) return 'use_crew_app';
  if (d.includes('RUTA_CUSTOMER_EMAIL')) return 'customer_email';
  // Pressing Cancel at Google comes back as access_denied with no trigger code.
  if (error === 'access_denied') return 'cancelled';
  return 'failed';
}

export const SOCIAL_SIGN_IN_COPY: Record<Exclude<SocialSignInOutcome, 'cancelled' | 'linked'>, string> = {
  email_not_verified: "Google didn't confirm that email address. Sign in with your email and password instead.",
  finish_email_signup:
    'This email started signing up but never entered its code. Finish that signup, or reset your password, then use Google.',
  prove_email:
    'Google can\'t open this account yet. Log in with your email and password, or tap "Forgot your password?" and reset it once. After that, Continue with Google works.',
  account_disabled: 'This account is turned off. Ask your company owner to turn it back on.',
  use_crew_app: 'This account uses the Ruta Crew app. Download the app and sign in with your phone number.',
  customer_email:
    "This email belongs to a customer account, so it can't open the office app. Customers sign in on their company's portal.",
  failed: "We couldn't sign you in with Google. Try again, or use your email and password.",
};

/** The same messages, naming the provider the person actually used. */
export function socialSignInMessage(
  outcome: Exclude<SocialSignInOutcome, 'cancelled' | 'linked'>,
  provider: SocialProvider = 'Google',
): string {
  const copy = SOCIAL_SIGN_IN_COPY[outcome];
  return provider === 'Google' ? copy : copy.replace(/Google/g, SOCIAL_PROVIDER_LABEL[provider]);
}

/**
 * After a link, Cognito refuses that one sign-in on purpose; signing in again
 * lands on the linked account. Allow exactly one automatic retry per tab.
 */
export function claimLinkRetry(): boolean {
  if (sessionStorage.getItem(LINK_RETRY_KEY)) return false;
  sessionStorage.setItem(LINK_RETRY_KEY, '1');
  return true;
}

export function clearLinkRetry() {
  sessionStorage.removeItem(LINK_RETRY_KEY);
}

/** True when this session started on a social sign-in (no password exists). */
export function markSocialSession(provider: SocialProvider) {
  safeLocalStorage.setItem('ruta_sign_in_method', provider.toLowerCase());
}

/** Google or Apple sign-in: no password was ever made for this login (RUT-4518). */
export function isSocialSignInSession(): boolean {
  const method = safeLocalStorage.getItem('ruta_sign_in_method');
  return method === 'google' || method === 'signinwithapple';
}
